import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/common/Navbar';
import AppearanceOptions, { themeLabel } from '../../components/common/AppearanceOptions';
import { MenuRow, ExpandRow } from '../../components/common/ProfileRows';
import ApprovalSheet from '../../components/driver/ApprovalSheet';
import VehicleSheet from '../../components/driver/VehicleSheet';
import DriverDocuments from '../../components/driver/DriverDocuments';
import LanguagePicker from '../../components/driver/LanguagePicker';
import SheetModal from '../../components/common/SheetModal';
import { languageLabel } from '../../utils/driverDocuments';
import { useTheme } from '../../contexts/ThemeContext';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { useCardFeature } from '../../hooks/useCardFeature';
import DriverTabBar from '../../components/driver/DriverTabBar';
import InvoiceProfileCard from '../../components/driver/InvoiceProfileCard';
import CarrierMembershipCard from '../../components/driver/CarrierMembershipCard';
import AddressInput from '../../components/common/AddressInput';
import { COLORS, FONTS } from '../../styles/tokens';
import { logOut } from '../../firebase/authService';
import { BADGE_CONFIG, computeBadge } from '../../utils/driverBadge';
import { buildApprovalItems } from '../../utils/driverApproval';
import { isInvoiceProfileComplete } from '../../utils/invoiceProfile';
import { normalizeMapTheme } from '../../utils/mapTheme';
import { vehicleTypeLabel } from '../../utils/vehicleProfile';
import { EQUIPMENT_LABELS } from '../../utils/loadingEquipment';

const DRIVER_STATUS = {
  active:   'Godkjent',
  pending:  'Venter godkjenning',
  rejected: 'Avslått',
};

// Varselvalg lagres på brukeren (users.notificationPrefs) og leses av Cloud Functions.
// Uten lagret valg er alt på.
const NOTIF_OPTIONS = [
  { key: 'push',          label: 'Push-varsler',            hint: 'Varsler på telefonen. Innboksen i appen fylles uansett.' },
  { key: 'newOrders',     label: 'Nye oppdrag i nærheten',  hint: 'Når et oppdrag passer område, massetyper og utstyr.' },
  { key: 'cancellations', label: 'Kansellerte oppdrag',     hint: 'Når bestiller eller Tippi kansellerer et oppdrag du har tatt.' },
];

function Switch({ checked, onChange, label, hint, disabled }) {
  return (
    <div style={switchStyles.row}>
      <div style={{ minWidth: 0 }}>
        <div style={switchStyles.label}>{label}</div>
        <div style={switchStyles.hint}>{hint}</div>
      </div>
      <button
        type="button" role="switch" aria-checked={checked} aria-label={label}
        disabled={disabled} onClick={() => onChange(!checked)}
        style={{ ...switchStyles.track, background: checked ? COLORS.success : COLORS.border }}
      >
        <span style={{ ...switchStyles.knob, transform: checked ? 'translateX(20px)' : 'translateX(0)' }} />
      </button>
    </div>
  );
}

const switchStyles = {
  row:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 0', borderTop: `1px solid ${COLORS.border}` },
  label: { fontWeight: 600, fontSize: 15, color: COLORS.textDark },
  hint:  { fontSize: 12, color: COLORS.textLight, marginTop: 2, lineHeight: 1.4 },
  track: { position: 'relative', width: 48, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer', flexShrink: 0, padding: 0, transition: 'background 0.15s' },
  knob:  { position: 'absolute', top: 3, left: 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'transform 0.15s' },
};

export default function DriverProfile() {
  const navigate = useNavigate();
  const { currentUser, userDoc, refreshUserDoc } = useAuth();
  const { enabled: cardEnabled } = useCardFeature();
  const [vehicle, setVehicle] = useState(null);
  // Én utfoldet rad om gangen: 'faktura' | 'area' | 'notif' | 'appearance'
  const [openSection, setOpenSection] = useState(null);
  const toggleSection = key => setOpenSection(cur => (cur === key ? null : key));
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const { theme } = useTheme();
  const [notifError, setNotifError] = useState('');
  const [mapError, setMapError] = useState('');
  const location = useLocation();
  const { unreadCount } = useUnreadCount();
  const { userRoles, activeRole, setActiveRole, viewingAs, setViewingAs } = useAuth();
  const mapTheme = normalizeMapTheme(userDoc?.preferences?.mapTheme);
  const [homeLocation, setHomeLocation] = useState({ street: '', city: '', lat: null, lng: null });
  const [areaStatus, setAreaStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [loading, setLoading] = useState(true);

  // #faktura: folder ut og scroller til utbetaling og fakturering (lenket fra Inntekt, banneret og oppdragssperren)
  useEffect(() => {
    if (location.hash !== '#faktura' || loading) return;
    setOpenSection('faktura');
    document.getElementById('faktura')?.scrollIntoView?.({ block: 'start' });
  }, [location.hash, loading]);

  useEffect(() => {
    if (!currentUser) return;
    getDocs(query(collection(db, 'vehicles'), where('driverUid', '==', currentUser.uid)))
      .then(snap => { if (!snap.empty) setVehicle({ ...snap.docs[0].data(), id: snap.docs[0].id }); })
      .catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid)).then((snap) => {
      const data = snap.data() ?? {};
      if (data.homeLocation) {
        setHomeLocation({
          street: data.homeLocation.street ?? '',
          city: data.homeLocation.city ?? '',
          lat: data.homeLocation.lat,
          lng: data.homeLocation.lng,
        });
      }
      setLoading(false);
    });
  }, [currentUser]);

  // Lagres med én gang, så kartet bytter uten å trykke «Lagre profil»
  async function handleMapTheme(value) {
    if (!currentUser || value === mapTheme) return;
    setMapError('');
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { 'preferences.mapTheme': value });
      await refreshUserDoc();
    } catch {
      setMapError('Kunne ikke lagre kartvalget. Prøv igjen.');
    }
  }

  const prefs = userDoc?.notificationPrefs ?? {};
  const prefOn = key => prefs[key] !== false;

  async function setPref(key, value) {
    if (!currentUser) return;
    setNotifError('');
    try {
      // Nettleseren spør om lov første gang push skrus på
      if (key === 'push' && value && typeof Notification !== 'undefined' && Notification.permission === 'default') {
        await Notification.requestPermission();
      }
      await updateDoc(doc(db, 'users', currentUser.uid), { [`notificationPrefs.${key}`]: value });
      await refreshUserDoc();
    } catch {
      setNotifError('Kunne ikke lagre varselvalget. Prøv igjen.');
    }
  }

  async function handleSaveLanguages(langs) {
    await updateDoc(doc(db, 'users', currentUser.uid), { languages: langs });
    await refreshUserDoc();
  }

  async function handleSaveArea() {
    if (!currentUser) return;
    setAreaStatus('saving');
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        homeLocation: homeLocation.lat ? {
          street: homeLocation.street,
          city: homeLocation.city,
          lat: homeLocation.lat,
          lng: homeLocation.lng,
        } : null,
      });
      setAreaStatus('saved');
    } catch {
      setAreaStatus('error');
    }
  }

  function handleApprovalAction(key) {
    setApprovalOpen(false);
    if (key === 'vehicle') setVehicleOpen(true);
    else if (key === 'documents') setDocsOpen(true);
    else if (key === 'earnings') navigate('/driver/earnings');
    else if (key === 'invoice') {
      setOpenSection('faktura');
      setTimeout(() => document.getElementById('faktura')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
    }
  }

  if (loading) return null;

  const name = userDoc?.name ?? '';
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
  const badgeKey = computeBadge({
    completedOrders:        userDoc?.completedOrders ?? 0,
    cancellationTimestamps: userDoc?.cancellationTimestamps ?? [],
    tippiVerified:          userDoc?.driverStatus === 'active',
  });
  const badge = BADGE_CONFIG[badgeKey];
  const completed = userDoc?.completedOrders ?? 0;
  const loadingLabels = (Array.isArray(vehicle?.loadingEquipment) ? vehicle.loadingEquipment : []).map(k => EQUIPMENT_LABELS[k] ?? k);
  const tags = [vehicle?.capacityTons != null && `${vehicle.capacityTons} tonn`, ...loadingLabels].filter(Boolean);
  const invoiceComplete = isInvoiceProfileComplete(userDoc?.invoiceProfile);
  const approvalItems = buildApprovalItems({ userDoc, vehicle, cardEnabled });
  const notifOn = !!userDoc?.fcmToken && (typeof Notification === 'undefined' || Notification.permission !== 'denied');
  const languages = Array.isArray(userDoc?.languages) ? userDoc.languages : [];
  const area = homeLocation.city || homeLocation.street || 'Ikke satt';

  // Rollebytte, ny rolle og «vis som» (admin) lå tidligere i Navbar-panelet
  const driverActive  = userRoles.includes('driver')  && userDoc?.driverStatus  === 'active';
  const ordererActive = userRoles.includes('orderer') && userDoc?.ordererStatus === 'active';
  const roleLinks = [];
  if (!userRoles.includes('orderer')) {
    roleLinks.push({ icon: 'plus', label: 'Bli bestiller', onClick: () => navigate('/profile/add-role?role=orderer') });
  } else if (userDoc?.ordererStatus === 'rejected') {
    roleLinks.push({ icon: 'plus', label: 'Søk på nytt som bestiller', onClick: () => navigate('/profile/add-role?role=orderer') });
  }
  if (userRoles.includes('orderer') && userDoc?.ordererStatus === 'pending') {
    roleLinks.push({ icon: 'swap', label: 'Bestiller-rolle', value: 'Venter godkjenning' });
  }
  if (driverActive && ordererActive && activeRole !== 'orderer') {
    roleLinks.push({ icon: 'swap', label: 'Bytt til bestiller', onClick: () => { setActiveRole('orderer'); navigate('/orderer'); } });
  }
  if (userRoles.includes('admin')) {
    [['driver', 'Sjåfør', '/driver'], ['orderer', 'Bestiller', '/orderer'], ['admin', 'Admin', '/admin']].forEach(([v, l, p]) => {
      roleLinks.push({
        icon: 'swap', label: `Vis som ${l.toLowerCase()}`,
        value: (viewingAs === v || (v === 'admin' && !viewingAs)) ? 'Aktiv' : undefined,
        onClick: () => { setViewingAs(v); navigate(p); },
      });
    });
  }

  async function handleLogout() {
    await logOut();
    navigate('/login');
  }

  return (
    <div className="driver-ui" style={{ minHeight: '100vh', background: COLORS.bg, display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 16px', width: '100%', flex: 1 }}>
        <h1 style={styles.title}>Profil</h1>

        <div style={styles.header}>
          <div style={styles.avatar} aria-hidden="true">{initials || '?'}</div>
          <div style={{ minWidth: 0 }}>
            <div style={styles.name}>{name || 'Sjåfør'}</div>
            <div style={styles.badgeLine}>
              <span aria-hidden="true">{badge.icon}</span> <strong>{badge.label}</strong>
              <span style={{ color: COLORS.textLight }}> · {completed} oppdrag</span>
            </div>
          </div>
        </div>

        <button type="button" style={styles.vehicle} onClick={() => setVehicleOpen(true)} aria-label={`Endre kjøretøy: ${vehicle ? vehicleTypeLabel(vehicle.type) : 'ikke registrert'}`}>
          <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <span style={{ display: 'block' }}>
              <span style={styles.vehicleLabel}>Kjøretøy</span>
              <span style={styles.vehicleName}>{vehicle ? vehicleTypeLabel(vehicle.type) : 'Ikke registrert'}</span>
            </span>
            <span style={styles.vehicleSide}>
              {vehicle?.registrationPlate && <span style={styles.plate}>{vehicle.registrationPlate}</span>}
              <span style={styles.edit} aria-hidden="true">{vehicle ? 'Endre' : 'Legg til'} ›</span>
            </span>
          </span>
          {tags.length > 0 && (
            <span style={styles.tags}>
              {tags.map(t => <span key={t} style={styles.tag}>{t}</span>)}
            </span>
          )}
        </button>

        <div style={styles.list}>
          <MenuRow icon="shield" label="Dokumenter og godkjenning" value={DRIVER_STATUS[userDoc?.driverStatus] ?? '—'}
                   valueTone={userDoc?.driverStatus === 'active' ? 'success' : 'warning'} onClick={() => setApprovalOpen(true)} />
          {userDoc?.carrierOrgId ? (
            <CarrierMembershipCard />
          ) : (
            <ExpandRow name="faktura" anchorId="faktura" icon="card" label="Utbetaling og fakturering"
                       value={invoiceComplete ? 'Komplett' : 'Mangler'} valueTone={invoiceComplete ? 'success' : 'warning'}
                       open={openSection === 'faktura'} onToggle={() => toggleSection('faktura')}>
              <InvoiceProfileCard embedded />
            </ExpandRow>
          )}
          <MenuRow icon="chat" label="Språk" value={languages.map(languageLabel).join(', ') || 'Ikke valgt'}
                   onClick={() => setLanguagesOpen(true)} />
          <ExpandRow name="area" icon="pin" label="Arbeidsområde" value={area}
                     open={openSection === 'area'} onToggle={() => toggleSection('area')}>
            <p style={{ ...styles.cardText, marginTop: 0 }}>
              Brukes til å matche oppdrag i ditt område. Du vil bare varsles om oppdrag innenfor din maks-radius.
            </p>
            <AddressInput
              label="Din base-adresse"
              value={homeLocation}
              onChange={v => { setAreaStatus('idle'); setHomeLocation(v); }}
            />
            {areaStatus === 'error' && <p role="alert" style={styles.notifWarn}>Kunne ikke lagre arbeidsområdet. Prøv igjen.</p>}
            {areaStatus === 'saved' && <p role="status" style={styles.areaSaved}>Lagret</p>}
            <button type="button" className="tippi-btn" style={styles.areaSave} onClick={handleSaveArea} disabled={areaStatus === 'saving'}>
              {areaStatus === 'saving' ? 'Lagrer…' : 'Lagre arbeidsområde'}
            </button>
          </ExpandRow>
          <ExpandRow name="notif" icon="bell" label="Varsler"
                     value={unreadCount > 0 ? `${unreadCount} uleste` : (notifOn && prefOn('push') ? 'På' : 'Av')}
                     open={openSection === 'notif'} onToggle={() => toggleSection('notif')}>
            {NOTIF_OPTIONS.map(o => (
              <Switch key={o.key} label={o.label} hint={o.hint} checked={prefOn(o.key)} onChange={v => setPref(o.key, v)} />
            ))}
            {typeof Notification !== 'undefined' && Notification.permission === 'denied' && (
              <p style={styles.notifWarn}>Push er blokkert i nettleseren. Slå det på i nettleserens innstillinger for dette nettstedet.</p>
            )}
            {notifError && <p role="alert" style={styles.notifWarn}>{notifError}</p>}
            <button style={styles.inboxBtn} onClick={() => navigate('/driver/varsler')}>
              Se alle varsler{unreadCount > 0 ? ` (${unreadCount} uleste)` : ''}
            </button>
          </ExpandRow>
          <MenuRow icon="list" label="Historikk og kansellerte" onClick={() => navigate('/driver/orders')} />
        </div>

        <h2 style={styles.sectionTitle}>Konto</h2>
        <div style={styles.list}>
          {roleLinks.map(r => <MenuRow key={r.label} icon={r.icon} label={r.label} value={r.value} onClick={r.onClick} />)}
          <ExpandRow name="utseende" icon="sun" label="Utseende" value={themeLabel(theme)}
                     open={openSection === 'appearance'} onToggle={() => toggleSection('appearance')}>
            <AppearanceOptions mapTheme={mapTheme} onMapTheme={handleMapTheme} />
            {mapError && <p role="alert" style={styles.notifWarn}>{mapError}</p>}
          </ExpandRow>
          <MenuRow icon="chat" label="Gi tilbakemelding" onClick={() => navigate('/driver/tilbakemelding')} />
          <MenuRow icon="lock" label="Personvern" onClick={() => navigate('/personvern')} />
          <MenuRow icon="out" label="Logg ut" onClick={handleLogout} danger />
        </div>

      </div>
      <DriverTabBar />
      {approvalOpen && <ApprovalSheet items={approvalItems} onClose={() => setApprovalOpen(false)} onAction={handleApprovalAction} />}
      {vehicleOpen && <VehicleSheet vehicle={vehicle} onClose={() => setVehicleOpen(false)} onSaved={setVehicle} />}
      {docsOpen && (
        <SheetModal onClose={() => setDocsOpen(false)} label="Dokumenter">
          <DriverDocuments userDoc={userDoc} uid={currentUser.uid} onChanged={refreshUserDoc} onClose={() => setDocsOpen(false)} />
        </SheetModal>
      )}
      {languagesOpen && (
        <SheetModal onClose={() => setLanguagesOpen(false)} label="Språk">
          <LanguagePicker value={languages} onSave={handleSaveLanguages} onClose={() => setLanguagesOpen(false)} />
        </SheetModal>
      )}
    </div>
  );
}

const styles = {
  title:        { fontFamily: FONTS.display, fontSize: 40, lineHeight: 1, fontWeight: 700, margin: '0 0 16px', color: COLORS.textDark },
  header:       { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 },
  avatar:       { width: 76, height: 76, borderRadius: '50%', flexShrink: 0, background: COLORS.surfaceHover, color: COLORS.textMid, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.display, fontSize: 30, fontWeight: 700, border: `1px solid ${COLORS.border}` },
  name:         { fontFamily: FONTS.display, fontSize: 30, lineHeight: 1.05, fontWeight: 700, color: COLORS.textDark },
  badgeLine:    { fontSize: 13, color: COLORS.textDark, marginTop: 2 },
  vehicle:      { background: COLORS.heroBg, border: `1px solid ${COLORS.heroBorder}`, color: '#fff', borderRadius: 22, padding: 18, marginBottom: 14, display: 'block', width: '100%', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' },
  vehicleSide:  { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 },
  edit:         { fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.85)' },
  vehicleLabel: { display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.65)' },
  vehicleName:  { display: 'block', fontFamily: FONTS.display, fontSize: 28, fontWeight: 700, lineHeight: 1.1, marginTop: 2 },
  plate:        { fontFamily: 'var(--font-mono)', fontSize: 12, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '6px 10px', whiteSpace: 'nowrap' },
  tags:         { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 },
  tag:          { background: COLORS.heroPillBg, color: COLORS.heroPillText, fontWeight: 700, fontSize: 12, borderRadius: 14, padding: '5px 11px' },
  list:         { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: '0 16px', marginBottom: 24 },
  sectionTitle: { fontFamily: FONTS.display, fontSize: 28, fontWeight: 700, margin: '0 4px 10px', color: COLORS.textDark, scrollMarginTop: 70 },
  areaSave:     { width: '100%', height: 48, borderRadius: 24, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 12 },
  areaSaved:    { color: COLORS.successText, fontSize: 14, fontWeight: 600, margin: '10px 0 0' },
  notifWarn:    { fontSize: 12, color: COLORS.warningText, background: COLORS.warningBg, borderRadius: 10, padding: '8px 12px', margin: '4px 0 8px' },
  inboxBtn:     { width: '100%', height: 46, borderRadius: 23, border: `2px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 4 },
  cardText:     { fontSize: 13, color: COLORS.textMid, margin: '0 0 12px', lineHeight: 1.5 },
};
